/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/

import {Component, ElementRef, Input, OnDestroy, OnInit, TemplateRef, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {InputDropdownComponent} from '../input-dropdown/input-dropdown.component';
import {UntypedFormBuilder} from '@angular/forms';
import {merge, Observable, timer} from 'rxjs';
import {debounceTime, switchMap} from 'rxjs/operators';
import {NgbTypeaheadSelectItemEvent} from '@ng-bootstrap/ng-bootstrap';
import {DropdownButtonModel} from '../../../spo/spo-create-new/step-material-list/material-list-modal/models/dropdown-button.model';

export interface ResultTemplateContext<T = any> {
    result: T;
    term: string;
}


@Component({
    selector: 'app-input-autocomplete',
    templateUrl: './input-autocomplete.component.html',
    styleUrls: [
        '../input/input.component.css',
        './input-autocomplete.component.css'
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
// Class must implement lifecycle hooks because it is metadata for Angular
export class InputAutocompleteComponent extends InputDropdownComponent implements OnInit, OnDestroy {
    @Input() resultTemplate: TemplateRef<ResultTemplateContext>;
    @Input() hasInactiveOptions: boolean;
    @Input() messageInactive: string;
    @Input() dropdownDisabled: boolean;

    @ViewChild('nativeHtmlInput') nativeHtmlInput: ElementRef;

    get hasResultTemplate(): boolean {
        return this.resultTemplate != null;
    }

    get inactiveOptionDataCy(): string {
        return `${this.baseCypressTag}-inactive-option`;
    }

    constructor(protected formBuilder: UntypedFormBuilder) {
        super(formBuilder);
    }

    tooltipInactiveMessage(result: string): string[] {
        const messages = [];
        if (this.messageInactive != null) {
            messages.push(this.messageInactive);
        }
        messages.push(result);
        return messages;
    }

    showInactiveTemplate(result: any): boolean {
        return this.dropdownDisabled || this.hasInactiveOptions && !result.active;
    }

    dropdownValueIsTemplateRef(value: any): boolean {
        return value instanceof TemplateRef;
    }

    reloadDropdownList(): void {
        const inputElement = this.nativeHtmlInput.nativeElement;
        inputElement.dispatchEvent(new Event('input'));
        inputElement.focus();
    }

    search = (text: Observable<string>): Observable<any[]> => {
        const debouncedText = text.pipe(debounceTime(this.DEBOUNCE_TIME));
        return merge(debouncedText, this.inputFocus)
                .pipe(switchMap(this.resultsFunction));
    };

    customSelectItem(event: NgbTypeaheadSelectItemEvent<DropdownButtonModel>): void {
        if (this.dropdownDisabled) {
            event.preventDefault();
            timer(0).subscribe(() => {
                this.reloadDropdownList();
            });
        } else if (this.dropdownValueIsTemplateRef(event.item?.template)) {
            event.preventDefault();
            timer(0).subscribe(() => {
                event.item?.action(null);
            });
        }
    }
}
